/**
 * Shared utility functions for reading and parsing URL parameters
 * Can be used across all pages (mobile, desktop, checkout)
 */

/**
 * Parse date from any format: DD-MM-YYYY, MM-DD-YYYY, YYYY-MM-DD, DD/MM/YYYY, YYYY/MM/DD, DD.MM.YYYY
 * Handles -, /, . separators
 * Smart detection: uses day>12 heuristic + optional format hint for ambiguous dates
 * @param {string} dateString - Date string from URL
 * @param {string} formatHint - 'MM-DD' for Google/American format, 'DD-MM' for default (optional)
 * @returns {Date|null} Parsed date or null
 */
export const parseDateFromURL = (dateString, formatHint = 'MM-DD') => {
  if (!dateString) return null;
  try {
    // Normalize: replace / and . with -
    const normalized = dateString.replace(/[/.]/g, '-');
    const parts = normalized.split('-').filter(Boolean);
    if (parts.length !== 3) return new Date(dateString);

    // Detect format by first part length
    if (parts[0].length === 4) {
      // YYYY-MM-DD (unambiguous)
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
      return new Date(year, month, day);
    } else {
      const first = parseInt(parts[0]);
      const second = parseInt(parts[1]);
      const year = parseInt(parts[2]);
      if (isNaN(first) || isNaN(second) || isNaN(year)) return null;

      // Smart detection for DD-MM-YYYY vs MM-DD-YYYY
      if (first > 12) {
        // first > 12 → must be day → DD-MM-YYYY (e.g., 25-03-2026)
        return new Date(year, second - 1, first);
      } else if (second > 12) {
        // second > 12 → must be day → MM-DD-YYYY (e.g., 03-25-2026)
        return new Date(year, first - 1, second);
      } else {
        // Both ≤ 12 → ambiguous → use formatHint
        if (formatHint === 'MM-DD') {
          // MM-DD-YYYY (Google/American format)
          return new Date(year, first - 1, second);
        } else {
          // DD-MM-YYYY (default)
          return new Date(year, second - 1, first);
        }
      }
    }
  } catch (e) {
    return null;
  }
};

/**
 * Create date at midnight to avoid timezone issues
 * @param {Date|string} date - Date object or string
 * @returns {Date} Date at midnight
 */
export const createDateAtMidnight = (date) => {
  if (date instanceof Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
  if (typeof date === 'string') {
    // Normalize: replace / and . with -
    const normalized = date.replace(/[/.]/g, '-');
    const parts = normalized.split('-').filter(Boolean);
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        // YYYY-MM-DD (unambiguous)
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      } else {
        const first = parseInt(parts[0]);
        const second = parseInt(parts[1]);
        const year = parseInt(parts[2]);
        // Smart detection: if first > 12 → DD-MM, if second > 12 → MM-DD
        if (first > 12) {
          return new Date(year, second - 1, first); // DD-MM-YYYY
        } else if (second > 12) {
          return new Date(year, first - 1, second); // MM-DD-YYYY
        } else {
          return new Date(year, first - 1, second); // MM-DD-YYYY default
        }
      }
    }
    return new Date(date);
  }
  return null;
};

/**
 * Read and parse all URL parameters from searchParams
 * @param {URLSearchParams} searchParams - React Router searchParams object
 * @returns {Object} Parsed URL parameters
 */
export const getURLParams = (searchParams) => {
  const checkin = searchParams.get('checkin');
  const checkout = searchParams.get('checkout');
  const adult = searchParams.get('adult');
  const child = searchParams.get('child');
  const childAges = searchParams.get('childAges'); // Comma-separated child ages
  const hotel_id = searchParams.get('hotel_id');
  const accommodationid = searchParams.get('accommodationid'); // Legacy support
  const promo = searchParams.get('promo');
  const profileid = searchParams.get('profileid');
  const multilanguageid = searchParams.get('multilanguageid');
  const currency = searchParams.get('currency');
  const group_id = searchParams.get('group_id');
  // Sanitize ratecode - remove any appended query strings (e.g., ?response=...)
  const rawRatecode = searchParams.get('ratecode');
  const ratecode = rawRatecode ? rawRatecode.split('?')[0] : null;

  // Detect Google source — Google Hotel Ads sends dates in MM-DD-YYYY format
  const isGoogleSource = searchParams.has('gclid') || searchParams.has('srsltid') ||
    (searchParams.get('utm_source') || '').toLowerCase().includes('google') ||
    (searchParams.get('utm_campaign') || '').toLowerCase().includes('hoteladv') ||
    (searchParams.get('utm_campaign') || '').toLowerCase().includes('hotelads');
  const dateHint = isGoogleSource ? 'MM-DD' : 'DD-MM';

  // Parse child ages from comma-separated string
  const parsedChildAges = childAges
    ? childAges.split(',').map(age => parseInt(age.trim())).filter(age => !isNaN(age))
    : [];

  // Get accommodation ID (priority: hotel_id > accommodationid for backward compatibility)
  const accommodationId = hotel_id || accommodationid || null;

  return {
    checkin: checkin ? parseDateFromURL(checkin, dateHint) : null,
    checkout: checkout ? parseDateFromURL(checkout, dateHint) : null,
    adult: adult ? parseInt(adult) : null,
    child: child ? parseInt(child) : null,
    childAges: parsedChildAges,
    hotel_id: accommodationId,
    accommodationid: accommodationId, // Keep for backward compatibility
    promo: promo || null,
    profileid: profileid || null,
    multilanguageid: multilanguageid ? parseInt(multilanguageid) : null,
    currency: currency || null,
    group_id: group_id ? parseInt(group_id) : null,
    ratecode: ratecode || null
  };
};

/**
 * Get accommodation ID from URL parameters
 * Priority: hotel_id > accommodationid (for backward compatibility)
 * @param {URLSearchParams} searchParams - React Router searchParams object
 * @param {string} defaultId - Default accommodation ID if not in URL
 * @returns {string} Accommodation ID
 */
export const getAccommodationIdFromURL = (searchParams, defaultId = '') => {
  const hotel_id = searchParams.get('hotel_id');
  const accommodationid = searchParams.get('accommodationid'); // Legacy support
  return hotel_id || accommodationid || defaultId;
};

